// @ts-nocheck
/**
 * App shell progressive enhancements. Everything here is optional: links, tab states,
 * the settings sheet (native popover) and content all work without JavaScript.
 *  - slide direction for tab view transitions (based on tab order)
 *  - keep the active feed tab centered in the top bar
 *  - close the settings sheet when one of its actions opens another dialog
 *  - app menu: the "Alle Kategorien" panel and Hell / Dunkel (snippets/app-menu-sheet.liquid)
 *  - motion feedback (styles: "Motion" in app-shell.css): tab icon springs in on arrival, Likes heart beats on a
 *    new like, a sliding thumb for the top bar tabs
 *  - tab feedback: the lit circle moves to a tab as soon as a finger is on it; while the next page loads (in-page
 *    via assets/app-router.js, or a page load) the page dims and a loader shows (styles: "Loading" in app-shell.css).
 *    window.vhpAppShell.busy(true | false) for the router.
 *  - desktop stage: the swipe card's photo glows behind the phone column (.app-ambient, "Desktop stage" in app-shell.css)
 */
(() => {
  const TAB_ORDER = ['swipe', 'explore', 'plan', 'likes', 'profile'];
  const DIR_KEY = 'app-shell-dir';
  const TAB_KEY = 'app-shell-tab';

  const currentTab = () => document.querySelector('.app-shell')?.dataset.appTab || null;

  /* ---------------------------------------------------------------- tab press + loading */

  // The lit circle on the pressed tab right away (pointerdown), back where it was if the tap does not happen.
  // The page / router sets the real state when the switch is done.
  let pressed = null;
  const paintTabs = (tab) => {
    document.querySelectorAll('[data-app-tabbar] .app-tab').forEach((link) => link.classList.toggle('is-active', link === tab));
  };
  const unpress = () => {
    if (!pressed) return;
    const { before } = pressed;
    pressed = null;
    if (before) paintTabs(before);
  };
  document.addEventListener(
    'pointerdown',
    (event) => {
      const tab = event.target.closest?.('[data-app-tabbar] .app-tab');
      if (!tab || event.button || tab.classList.contains('is-active')) return;
      pressed = { tab, before: document.querySelector('[data-app-tabbar] .app-tab.is-active') };
      paintTabs(tab);
    },
    { capture: true, passive: true }
  );
  document.addEventListener('pointercancel', unpress, true);
  // Released on the tab: it is a tap, the click switches (router or page load). Released elsewhere: no tap.
  document.addEventListener(
    'pointerup',
    (event) => {
      if (!pressed) return;
      if (event.target.closest?.('[data-app-tabbar] .app-tab') === pressed.tab) pressed = null;
      else unpress();
    },
    true
  );

  // The lit circle travels to the new tab, whatever made it change: a press, the router, back / forward, a revert.
  // It is one circle per tab, so the new one starts where the old one was (or is, mid-glide) and slides over.
  const GLIDE_MS = 380; // and --app-ease, as the cross-page glide always had
  const glideTabbar = document.querySelector('[data-app-tabbar]');
  if (glideTabbar && 'animate' in Element.prototype) {
    const centerX = (tab) => {
      const r = tab.getBoundingClientRect();
      return r.left + r.width / 2;
    };
    let current = glideTabbar.querySelector('.app-tab.is-active');
    let glide = null; // { anim, from, to } of the running glide
    const visualX = () => {
      if (glide && glide.anim.playState === 'running') {
        const t = Math.min(1, (glide.anim.currentTime || 0) / GLIDE_MS);
        const eased = 1 - Math.pow(1 - t, 2.6); // close to --app-ease
        return glide.from + (glide.to - glide.from) * eased;
      }
      return current ? centerX(current) : null;
    };
    new MutationObserver(() => {
      const next = glideTabbar.querySelector('.app-tab.is-active');
      if (next === current) return;
      const from = visualX();
      glide?.anim.cancel();
      glide = null;
      current = next;
      const lit = next?.querySelector('.app-tab__lit');
      if (!lit || from == null || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      const to = centerX(next);
      if (Math.abs(from - to) < 1) return;
      const anim = lit.animate([{ transform: `translateX(${from - to}px)` }, { transform: 'none' }], {
        duration: GLIDE_MS,
        easing: 'cubic-bezier(0.2, 0.8, 0.2, 1)',
      });
      glide = { anim, from, to };
    }).observe(glideTabbar, { subtree: true, attributes: true, attributeFilter: ['class'] });
  }

  // Busy: after BUSY_DELAY the page fades back a little and the story line runs under the top bar; the loader
  // pill comes up later (CSS). Fast switches never show it. busy(false, true): off at once, no fade, for the
  // moment a view transition is about to capture the old page.
  const BUSY_DELAY = 120;
  let busyTimer = 0;
  let busyEl = null;
  const busy = (on, instant) => {
    window.clearTimeout(busyTimer);
    const root = document.documentElement;
    if (!on) {
      if (instant && root.classList.contains('is-app-busy')) {
        root.classList.add('is-app-unbusy');
        requestAnimationFrame(() => requestAnimationFrame(() => root.classList.remove('is-app-unbusy')));
      }
      root.classList.remove('is-app-busy');
      document.getElementById('MainContent')?.removeAttribute('aria-busy');
      return;
    }
    if (!busyEl) {
      busyEl = document.createElement('div');
      busyEl.className = 'app-busy';
      busyEl.setAttribute('aria-hidden', 'true');
      const loader = '<i></i><i></i><i></i>';
      busyEl.innerHTML = `<span class="app-busy__bar app-loader">${loader}</span><span class="app-busy__puck"><span class="app-loader">${loader}</span></span>`;
      (document.querySelector('.app-phone') || document.body).append(busyEl);
    }
    busyTimer = window.setTimeout(() => {
      root.classList.add('is-app-busy');
      document.getElementById('MainContent')?.setAttribute('aria-busy', 'true');
    }, BUSY_DELAY);
  };
  window.vhpAppShell = { busy };

  // A tab that is a page load (Plan, every tab on the planner page): busy until the next page takes over.
  // The router has already taken its tabs (preventDefault) by the time this bubbles up.
  document.addEventListener('click', (event) => {
    const tab = event.target.closest?.('[data-app-tabbar] .app-tab');
    if (!tab || event.defaultPrevented || event.button || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    if (tab.dataset.tab === currentTab()) return;
    paintTabs(tab);
    busy(true);
  });
  // Back here from the next page (bfcache): nothing is loading any more.
  window.addEventListener('pageshow', (event) => {
    if (!event.persisted) return;
    busy(false);
    paintTabs(document.querySelector(`[data-app-tabbar] .app-tab[data-tab="${currentTab()}"]`));
  });

  const setDir = (dir) => {
    if (dir) document.documentElement.dataset.appDir = dir;
    else delete document.documentElement.dataset.appDir;
  };

  // Old page: remember where we are and which tab was tapped. The tab bar links are the only
  // thing that sets a direction; everything else (recipe -> back, etc.) keeps the default fade.
  document.addEventListener(
    'click',
    (event) => {
      const link = event.target.closest?.('.app-tab');
      if (!link) return;
      try {
        const from = TAB_ORDER.indexOf(currentTab());
        const to = TAB_ORDER.indexOf(link.dataset.tab);
        if (from === -1 || to === -1 || from === to) {
          sessionStorage.removeItem(DIR_KEY);
        } else {
          sessionStorage.setItem(DIR_KEY, to > from ? 'forward' : 'back');
        }
      } catch (_) {}
    },
    { capture: true }
  );

  // pageswap: the old document is still showing, so set the direction for the "old" animation.
  // The loader goes too, so the old page leaves as itself (only with a transition: without one the next page
  // replaces this one anyway, and an undimmed frame in between would flash).
  window.addEventListener('pageswap', (event) => {
    if (event.viewTransition) busy(false, true);
    try {
      setDir(sessionStorage.getItem(DIR_KEY));
    } catch (_) {}
  });

  // pagereveal: the new document sets the direction for its "new" animation, then clears it.
  window.addEventListener('pagereveal', (event) => {
    let dir = null;
    try {
      dir = sessionStorage.getItem(DIR_KEY);
      sessionStorage.removeItem(DIR_KEY);
    } catch (_) {}
    setDir(dir);
    const vt = event.viewTransition;
    if (vt) vt.finished.finally(() => setDir(null));
    else setDir(null);
    // Arrived through the tab bar: the tapped icon springs back out (it was pressed in on the old page).
    if (dir) {
      const tab = document.querySelector('.app-tab.is-active');
      tab?.classList.add('is-arriving');
      tab?.addEventListener('animationend', () => tab.classList.remove('is-arriving'), { once: true });
    }
  });

  // A new like (swipe, reel heart, undo): the Likes tab heart beats once, with a light tap on Android.
  // Silent changes (account sync) and removals stay quiet.
  let likeTotal = null;
  const countLikes = () => {
    try {
      return window.vhpPlannerLikes?.likeCount() ?? null;
    } catch (_) {
      return null;
    }
  };
  window.addEventListener('vhp:likes-changed', (event) => {
    const before = likeTotal;
    likeTotal = countLikes();
    if (event.detail?.silent || before == null || likeTotal == null || likeTotal <= before) return;
    const tab = document.querySelector('.app-tab[data-tab="likes"]');
    if (!tab) return;
    tab.classList.remove('is-bump');
    void tab.offsetWidth; // restart when likes come in quick succession
    tab.classList.add('is-bump');
    window.clearTimeout(tab.bumpTimer);
    tab.bumpTimer = window.setTimeout(() => tab.classList.remove('is-bump'), 700);
    try {
      navigator.vibrate?.(12);
    } catch (_) {}
  });

  // Top bar tabs: one thumb that slides to the active tab (feed tabs, the planner's segmented control).
  // The pages only toggle .is-active; the observer follows.
  const initThumb = (nav) => {
    if (nav.dataset.thumbBound) return;
    nav.dataset.thumbBound = '1';
    const thumb = document.createElement('span');
    thumb.className = 'app-topbar__thumb';
    thumb.setAttribute('aria-hidden', 'true');
    nav.prepend(thumb);
    const place = () => {
      const active = nav.querySelector('.app-feed-tab.is-active');
      if (!active || !active.offsetWidth) return;
      nav.style.setProperty('--thumb-x', `${active.offsetLeft}px`);
      nav.style.setProperty('--thumb-w', `${active.offsetWidth}px`);
      if (!nav.classList.contains('has-thumb')) nav.classList.add('has-thumb');
    };
    place();
    // Transitions only after the first placement, so the thumb does not fly in on load.
    requestAnimationFrame(() => requestAnimationFrame(() => nav.classList.add('is-thumb-live')));
    let last = nav.querySelector('.app-feed-tab.is-active');
    const tabs = nav.querySelectorAll('.app-feed-tab');
    const observer = new MutationObserver(() => {
      const active = nav.querySelector('.app-feed-tab.is-active');
      if (active === last) return;
      last = active;
      place();
      if (active && nav.scrollWidth > nav.clientWidth) {
        nav.scrollTo({ left: active.offsetLeft - (nav.clientWidth - active.offsetWidth) / 2, behavior: 'smooth' });
      }
    });
    tabs.forEach((tab) => observer.observe(tab, { attributes: true, attributeFilter: ['class'] }));
    if ('ResizeObserver' in window) new ResizeObserver(place).observe(nav);
    document.fonts?.ready.then(place);
  };

  const centerActiveFeedTab = () => {
    const feeds = document.querySelector('.app-topbar__feeds');
    const active = feeds?.querySelector('.is-active');
    if (!feeds || !active) return;
    feeds.scrollLeft = active.offsetLeft - (feeds.clientWidth - active.offsetWidth) / 2;
  };

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest?.('[data-app-close-sheet]');
    if (!trigger) return;
    trigger.closest('[popover]')?.hidePopover?.();
  });

  /* ---------------------------------------------------------------- app menu */

  // snippets/app-menu-sheet.liquid (docs/NAV_MENU.md): "Alle Kategorien" slides the second panel in inside the sheet
  // and the sheet's height follows; every opening starts on the first panel. Hell / Dunkel via window.vhpTheme.
  const initMenu = (menu) => {
    if (menu.dataset.menuBound) return;
    menu.dataset.menuBound = '1';
    const panels = { root: menu.querySelector('[data-menu-panel="root"]'), all: menu.querySelector('[data-menu-panel="all"]') };
    const reduce = () => window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    let settle = 0;

    const show = (toAll, instant) => {
      const to = toAll ? panels.all : panels.root;
      const from = toAll ? panels.root : panels.all;
      if (!to || !from || menu.classList.contains('is-all') === toAll) return;
      window.clearTimeout(settle);
      const startH = menu.offsetHeight;
      to.hidden = false;
      to.inert = false;
      from.inert = true;
      void to.offsetWidth;
      menu.classList.toggle('is-all', toAll);
      menu.querySelectorAll('[data-menu-all]').forEach((btn) => btn.setAttribute('aria-expanded', String(toAll)));
      menu.scrollTop = 0;
      const done = () => {
        from.hidden = true;
        menu.classList.remove('is-resizing');
        menu.style.height = '';
      };
      if (instant || reduce()) {
        done();
      } else {
        menu.classList.remove('is-resizing');
        menu.style.height = '';
        const endH = menu.offsetHeight;
        menu.style.height = `${startH}px`;
        void menu.offsetWidth;
        menu.classList.add('is-resizing');
        menu.style.height = `${endH}px`;
        settle = window.setTimeout(done, 400);
      }
      if (!instant) {
        const focus = toAll ? to.querySelector('[data-menu-back]') : to.querySelector('.app-menu__more[data-menu-all]');
        focus?.focus({ preventScroll: true });
      }
    };

    const syncTheme = () => {
      const mode = window.vhpTheme?.get();
      menu.querySelectorAll('[data-app-theme-value]').forEach((btn) => {
        btn.setAttribute('aria-checked', String(btn.dataset.appThemeValue === mode));
      });
    };

    menu.addEventListener('click', (event) => {
      if (event.target.closest('[data-menu-all]')) show(true);
      else if (event.target.closest('[data-menu-back]')) show(false);
      const theme = event.target.closest('[data-app-theme-value]');
      if (theme) {
        window.vhpTheme?.set(theme.dataset.appThemeValue);
        syncTheme();
      }
    });
    menu.addEventListener('beforetoggle', (event) => {
      if (event.newState !== 'open') return;
      show(false, true);
      syncTheme();
    });
    // Back from a page the menu led to: the back-forward cache brings the page back with the menu still open.
    window.addEventListener('pageshow', (event) => {
      if (!event.persisted) return;
      try {
        if (menu.matches(':popover-open')) menu.hidePopover();
      } catch (e) {}
    });
    syncTheme();
  };

  const init = () => {
    centerActiveFeedTab();
    document.querySelectorAll('.app-topbar__feeds').forEach(initThumb);
    document.querySelectorAll('[data-app-menu]').forEach(initMenu);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
  // A tab switched in-page (assets/app-router.js) brings its own top bar.
  window.addEventListener('vhp:view-show', init);
  // The likes store is a later deferred script: count once everything has run.
  const seedLikes = () => {
    if (likeTotal == null) likeTotal = countLikes();
  };
  if (document.readyState === 'complete') seedLikes();
  else {
    document.addEventListener('DOMContentLoaded', seedLikes, { once: true });
    window.addEventListener('load', seedLikes, { once: true });
  }

  /* ---------------------------------------------------------------- planner progress (non-subscribers) */

  // Plan tab badge: "2/3" while the likes build up, ✓ once they make a plan, a dot once a preview is built and
  // waiting (the planner's own state in vhp_planner_anon). Top bar sparkle caption: Planer / Bereit / Fertig.
  // A waiting preview also tags the clicks as resume_tab / resume_top for the planner_entry step.
  const BUILT_SCREENS = new Set(['preview', 'subscribe', 'reviews']);
  let planKind = null;
  const subscriber = () => {
    try {
      return JSON.parse(document.getElementById('vhp-account-config')?.textContent || '{}').subscriber === true;
    } catch (e) {
      return false;
    }
  };
  const planProgress = () => {
    let saved = null;
    try {
      saved = JSON.parse(localStorage.getItem(window.vhpPlannerLikes?.STORAGE_KEY || 'vhp_planner_anon') || 'null');
    } catch (e) {
      saved = null;
    }
    if (saved && BUILT_SCREENS.has(saved.screen)) return { kind: 'built' };
    const api = window.vhpPlannerLikes;
    if (!api) return null;
    const doc = api.read();
    const n = api.likeCount(doc);
    // "0/3" from the first card: the tab reads as something to fill (vhp-planner docs/entry.md).
    if (!n) return { kind: 'progress', text: `0/${api.LIKE_READY}` };
    if (api.planReady(doc)) return { kind: 'ready' };
    const view = api.weekView(doc);
    const done = view.needMain ? Math.min(n, api.LIKE_READY - 1) : Math.min(n, api.LIKE_READY);
    return { kind: 'progress', text: `${done}/${api.LIKE_READY}` };
  };
  const paintPlan = () => {
    if (subscriber() || document.body.classList.contains('vhp-planner-page')) return;
    const state = planProgress();
    const kind = state?.kind || null;
    const badge = document.querySelector('[data-plan-badge]');
    if (badge) {
      badge.hidden = !kind;
      badge.className = `app-tab__plan-badge${kind ? ` is-${kind}` : ''}`;
      badge.textContent = kind === 'progress' ? state.text : kind === 'ready' ? '✓' : '';
      // Pulse when the likes just made a plan (not on arrival with one already there).
      if (kind === 'ready' && planKind === 'progress' && !reduceMotion()) {
        badge.classList.add('is-pulse');
        badge.addEventListener('animationend', () => badge.classList.remove('is-pulse'), { once: true });
      }
      badge.closest('.app-tab')?.setAttribute('data-track-source', kind === 'built' ? 'resume_tab' : 'tabbar');
    }
    document.querySelectorAll('[data-plan-state]').forEach((caption) => {
      caption.textContent = window.vhpT(kind === 'built' ? 'shell.state_built' : kind === 'ready' ? 'shell.state_ready' : 'shell.state_planner');
      caption.classList.toggle('is-on', kind === 'built' || kind === 'ready');
      caption.closest('a')?.setAttribute('data-track-source', kind === 'built' ? 'resume_top' : 'topbar');
    });
    planKind = kind;
  };
  const reduceMotion = () => window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  const startPlan = () => {
    paintPlan();
    window.addEventListener('vhp:likes-changed', paintPlan);
    window.addEventListener('vhp:view-show', paintPlan);
    window.addEventListener('pageshow', paintPlan);
    window.addEventListener('storage', (event) => {
      if (event.key === null || event.key === (window.vhpPlannerLikes?.STORAGE_KEY || 'vhp_planner_anon')) paintPlan();
    });
  };
  // The likes store is a later deferred script.
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', startPlan, { once: true });
  else startPlan();
})();

// Desktop stage: the swipe card on top lends its photo to the blurred glow behind the phone column. Two layers
// cross-fade; the deck says which card shows (vhp:deck-show, assets/vhp-swipe-deck.js). CSS shows it on the swipe tab only.
(() => {
  const ambient = document.querySelector('[data-app-ambient]');
  if (!ambient) return;
  const wide = window.matchMedia('(min-width: 600px)');
  const layers = [...ambient.children];
  let front = 0;
  let last = '';
  // A small rendition is plenty under an 80px blur (Shopify CDN images take ?width=).
  const small = (url) => {
    try {
      const u = new URL(url, location.href);
      if (/cdn\.shopify\.com|\/cdn\/shop\//.test(u.href)) {
        u.searchParams.set('width', '240');
        u.searchParams.delete('height');
        u.searchParams.delete('crop');
      }
      return u.href;
    } catch (e) {
      return url;
    }
  };
  document.addEventListener('vhp:deck-show', (event) => {
    const image = event.detail?.image;
    if (!wide.matches || !image || !(event.target instanceof Element) || !event.target.closest('.app-main')) return;
    const url = small(image);
    if (url === last) return;
    last = url;
    const img = new Image();
    img.src = url;
    const paint = () => {
      if (last !== url) return;
      front = 1 - front;
      layers[front].style.backgroundImage = `url("${url.replace(/"/g, '%22')}")`;
      layers[front].classList.add('is-on');
      layers[1 - front].classList.remove('is-on');
      ambient.classList.add('is-lit');
    };
    (img.decode ? img.decode() : Promise.resolve()).then(paint, paint);
  });
})();

/* Collection page's back arrow (snippets/app-topbar.liquid, data-app-back-explore): when Explore is the page
   before, a step back (it comes back as it was, scroll and rows); otherwise the link opens Explore. */
(() => {
  'use strict';
  document.addEventListener('click', (event) => {
    const back = event.target instanceof Element ? event.target.closest('a[data-app-back-explore]') : null;
    if (!back || event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
    try {
      const from = new URL(document.referrer);
      const explore = new URL(back.href, location.href);
      if (from.origin === location.origin && from.pathname.replace(/\/$/, '') === explore.pathname.replace(/\/$/, '') && history.length > 1) {
        event.preventDefault();
        history.back();
      }
    } catch (_) {}
  });
})();
